import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Badge,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventIcon from "@mui/icons-material/Event";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import TodayIcon from "@mui/icons-material/Today";
import { useNavigate } from "react-router-dom";
import { useGetBanquetBookingsMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */
const C = {
  navy:      "#0d1b2a",
  navyMid:   "#1a2f47",
  navySoft:  "#243852",
  gold:      "#c9a96e",
  goldPale:  "#f7f1e6",
  cream:     "#faf8f4",
  white:     "#ffffff",
  slate:     "#5c6b7e",
  slateLt:   "#8a97a6",
  border:    "#e4ddd3",
  bsoft:     "#ede8e0",
  greenBg:   "#edf7ee",
  greenTx:   "#2d6a35",
  greenBd:   "#a8d5ac",
  redBg:     "#fdf0ef",
  redTx:     "#9b2c2c",
  redBd:     "#f0a8a8",
  blueBg:    "#eef4fd",
  blueTx:    "#1e3f7a",
  blueBd:    "#a8c4f0",
  amberBg:   "#fef9ec",
  amberTx:   "#7c5a0a",
  amberBd:   "#f0d88a",
  highlight: "#ffebd3",
} as const;

const SHADOW_SM = "0 1px 4px rgba(13,27,42,.07)";
const SHADOW_MD = "0 4px 16px rgba(13,27,42,.10)";

/* ═══════════════════════════════════════════════════════════════════════════
   HALLS
   ═══════════════════════════════════════════════════════════════════════════ */
const HALLS = [
  { key: "N", label: "Nova",    capacity: 250, color: "#1565c0", bg: "#e3f0fc" },
  { key: "T", label: "Titan",   capacity: 250, color: "#2e7d32", bg: "#e6f4ea" },
  { key: "L", label: "Lunar",   capacity: 250, color: "#e65100", bg: "#fff3e0" },
  { key: "E", label: "Eclipse", capacity: 250, color: "#6a1b9a", bg: "#f3e5f5" },
] as const;

type HallKey = "N" | "T" | "L" | "E";

const HALL_KEY_MAP: Record<string, HallKey> = { nova: "N", titan: "T", lunar: "L", eclipse: "E" };

const DOW_FULL  = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DOW_TINY  = ["S", "M", "T", "W", "T", "F", "S"];

const pad2 = (n: number) => String(n).padStart(2, "0");
const fmtDate = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const fmt = (n: number) => n.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

const SLOT_LABEL: Record<string, string> = { AM: "AM", PM: "PM", FULL_DAY: "Full Day" };
const STATUS_COLOR: Record<string, "warning" | "success" | "info" | "default" | "error"> = {
  quote: "warning", order: "success", invoice: "info", complete: "default", cancel: "error",
};

/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */
interface BookingEntry {
  id: number;
  bookingRef?: string;
  banquetName?: string;
  eventDate: string;
  timeSlot: string;
  eventType?: string;
  status?: string;
  guestCount?: number;
  grandTotal?: number;
  agentName?: string;
  halls: { hallKey: string; hallLabel?: string; hallCapacity?: number }[];
  primaryCustomer?: { firstName?: string; lastName?: string; phone?: string } | null;
}

interface SlotInfo {
  bookings: { id: number; name: string }[];
}

type CalendarMap = Record<string, Record<HallKey, Record<"AM" | "PM", SlotInfo>>>;

/* ═══════════════════════════════════════════════════════════════════════════
   UTILITIES
   ═══════════════════════════════════════════════════════════════════════════ */
/**
 * Resolve a hallKey (single or composite like "Nova + Titan") into individual HallKeys.
 */
function resolveHallKeys(hallKey: string): HallKey[] {
  const raw = (hallKey ?? "").toLowerCase().trim();
  // Direct single-hall match
  if (HALL_KEY_MAP[raw]) return [HALL_KEY_MAP[raw]];
  // "full hall" → all halls
  if (raw === "full hall") return HALLS.map(h => h.key as HallKey);
  // Composite: "nova + titan", "titan + lunar + eclipse", etc.
  const parts = raw.split(/\s*\+\s*/);
  const keys: HallKey[] = [];
  for (const p of parts) {
    const mapped = HALL_KEY_MAP[p.trim()];
    if (mapped) keys.push(mapped);
  }
  return keys;
}

function buildCalendarMap(bookings: BookingEntry[]): CalendarMap {
  const map: CalendarMap = {};
  const ensure = (date: string, hk: HallKey, slot: "AM" | "PM") => {
    if (!map[date]) map[date] = {} as CalendarMap[string];
    if (!map[date][hk]) map[date][hk] = { AM: { bookings: [] }, PM: { bookings: [] } };
    return map[date][hk][slot];
  };
  for (const b of bookings) {
    const dateStr = b.eventDate?.split("T")[0] ?? "";
    if (!dateStr) continue;
    const rawSlot = (b.timeSlot ?? "").toUpperCase();
    const name = b.banquetName || `Booking #${b.id}`;
    const entry = { id: b.id, name };
    for (const h of b.halls ?? []) {
      const hallKeys = resolveHallKeys(h.hallKey);
      for (const hk of hallKeys) {
        if (rawSlot === "FULL_DAY" || rawSlot === "AM") ensure(dateStr, hk, "AM").bookings.push(entry);
        if (rawSlot === "FULL_DAY" || rawSlot === "PM") ensure(dateStr, hk, "PM").bookings.push(entry);
      }
    }
  }
  return map;
}

function getSlotState(info: SlotInfo | undefined): "free" | "booked" | "conflict" {
  if (!info || info.bookings.length === 0) return "free";
  if (info.bookings.length === 1) return "booked";
  return "conflict";
}

interface DayHallSlot { state: "free" | "booked" | "conflict"; bookings: { id: number; name: string }[] }
interface DayDetails {
  halls: Record<HallKey, { AM: DayHallSlot; PM: DayHallSlot }>;
  conflicts: { hall: string; slot: "AM" | "PM"; count: number }[];
  totalBookings: number;
  freeSlots: number;
}

function getDayDetails(calMap: CalendarMap, dateStr: string): DayDetails {
  const hallSlots = calMap[dateStr];
  const seenIds = new Set<number>();
  const conflicts: DayDetails["conflicts"] = [];
  let freeSlots = 0;
  const halls = {} as DayDetails["halls"];
  for (const h of HALLS) {
    const hk = h.key as HallKey;
    const am: DayHallSlot = { state: "free", bookings: [] };
    const pm: DayHallSlot = { state: "free", bookings: [] };
    if (hallSlots?.[hk]) {
      am.bookings = hallSlots[hk].AM?.bookings ?? [];
      pm.bookings = hallSlots[hk].PM?.bookings ?? [];
      am.state = getSlotState(hallSlots[hk].AM);
      pm.state = getSlotState(hallSlots[hk].PM);
      am.bookings.forEach((b) => seenIds.add(b.id));
      pm.bookings.forEach((b) => seenIds.add(b.id));
      if (am.state === "conflict") conflicts.push({ hall: h.label, slot: "AM", count: am.bookings.length });
      if (pm.state === "conflict") conflicts.push({ hall: h.label, slot: "PM", count: pm.bookings.length });
    }
    if (am.state === "free") freeSlots++;
    if (pm.state === "free") freeSlots++;
    halls[hk] = { AM: am, PM: pm };
  }
  return { halls, conflicts, totalBookings: seenIds.size, freeSlots };
}

/** Count bookings touching a date (for mobile badge) */
function countDayBookings(calMap: CalendarMap, dateStr: string): number {
  const hallSlots = calMap[dateStr];
  if (!hallSlots) return 0;
  const ids = new Set<number>();
  for (const hk of Object.keys(hallSlots) as HallKey[]) {
    hallSlots[hk]?.AM?.bookings?.forEach((b) => ids.add(b.id));
    hallSlots[hk]?.PM?.bookings?.forEach((b) => ids.add(b.id));
  }
  return ids.size;
}

/* ═══════════════════════════════════════════════════════════════════════════
   HALL SLOT (desktop day cell)
   ═══════════════════════════════════════════════════════════════════════════ */
const HallSlot: React.FC<{
  hallKey: HallKey; hallColor: string; info: SlotInfo | undefined;
  slot: "AM" | "PM"; dateStr: string;
  onFreeClick: (d: string, s: "AM" | "PM", h: HallKey) => void;
  onBookedClick: (d: string) => void;
  slotFilter?: "all" | "free" | "booked";
}> = React.memo(({ hallKey, hallColor, info, slot, dateStr, onFreeClick, onBookedClick, slotFilter = "all" }) => {
  const state = getSlotState(info);
  const dimmed = (slotFilter === "free" && state !== "free") || (slotFilter === "booked" && state === "free");
  const handleClick = () => state === "free" ? onFreeClick(dateStr, slot, hallKey) : onBookedClick(dateStr);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex", alignItems: "center", gap: "4px", px: "5px", py: "2px",
        borderRadius: "3px", bgcolor: state === "free" ? C.greenBg : C.redBg,
        border: state === "conflict" ? `1.5px solid ${C.redBd}` : "none",
        cursor: "pointer", transition: "all 0.15s", "&:hover": { filter: "brightness(0.92)" },
        minHeight: 18, overflow: "hidden",
        opacity: dimmed ? 0.2 : 1,
      }}
    >
      <Typography sx={{ fontSize: 8, fontWeight: 800, color: hallColor, lineHeight: 1, flexShrink: 0, width: 10, textAlign: "center" }}>{hallKey}</Typography>
      {state === "free" && <Typography sx={{ fontSize: 7, color: C.greenTx, lineHeight: 1 }}>Free</Typography>}
      {state === "booked" && info && <Typography noWrap sx={{ fontSize: 7, color: C.redTx, lineHeight: 1, maxWidth: 55, overflow: "hidden", textOverflow: "ellipsis" }}>{info.bookings[0].name}</Typography>}
      {state === "conflict" && info && <Typography sx={{ fontSize: 7, fontWeight: 700, color: C.redTx, lineHeight: 1 }}>{info.bookings.length} bookings</Typography>}
    </Box>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   DESKTOP DAY CELL
   ═══════════════════════════════════════════════════════════════════════════ */
const DayCell: React.FC<{
  day: number; dateStr: string; isToday: boolean; isWeekend: boolean; isSelected: boolean;
  hallSlots: CalendarMap[string] | undefined;
  onDayClick: (d: string) => void;
  onFreeSlotClick: (d: string, s: "AM" | "PM", h: HallKey) => void;
  onBookedSlotClick: (d: string) => void;
  slotFilter?: "all" | "free" | "booked";
}> = React.memo(({ day, dateStr, isToday, isWeekend, isSelected, hallSlots, onDayClick, onFreeSlotClick, onBookedSlotClick, slotFilter = "all" }) => {
  let hasConflict = false;
  let bookingCount = 0;
  if (hallSlots) {
    const ids = new Set<number>();
    for (const hk of Object.keys(hallSlots) as HallKey[]) {
      for (const s of ["AM", "PM"] as const) {
        const si = hallSlots[hk]?.[s];
        if (getSlotState(si) === "conflict") hasConflict = true;
        si?.bookings?.forEach((b) => ids.add(b.id));
      }
    }
    bookingCount = ids.size;
  }

  return (
    <Box
      sx={{
        borderRight: `1px solid ${C.bsoft}`, borderBottom: `1px solid ${C.bsoft}`,
        minHeight: 134, display: "flex", flexDirection: "column", cursor: "pointer",
        transition: "all 0.15s ease",
        bgcolor: isSelected ? C.highlight : C.white,
        outline: isSelected ? `2px solid ${C.gold}` : "none",
        outlineOffset: "-1px",
        "&:hover": { bgcolor: isSelected ? C.highlight : "#fef6ee" },
        "&:nth-of-type(7n)": { borderRight: "none" },
        position: "relative", zIndex: isSelected ? 1 : 0,
      }}
      onClick={() => onDayClick(dateStr)}
    >
      {/* Day number + badges */}
      <Box sx={{ px: "6px", pt: "5px", pb: "3px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: isToday ? C.gold : "transparent",
          color: isToday ? C.navy : isWeekend ? C.slateLt : C.navy,
        }}>
          <Typography sx={{ fontSize: 12, fontWeight: isToday ? 700 : 500, lineHeight: 1 }}>{day}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
          {hasConflict && (
            <Box sx={{ fontSize: 7, fontWeight: 700, color: C.redTx, bgcolor: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: "10px", px: "5px", py: "1px", lineHeight: 1.4 }}>!</Box>
          )}
          {bookingCount > 0 && (
            <Box sx={{ fontSize: 8, fontWeight: 600, color: C.navy, bgcolor: C.highlight, borderRadius: "10px", px: "5px", py: "1px", lineHeight: 1.4 }}>{bookingCount}</Box>
          )}
        </Box>
      </Box>

      {/* AM/PM headers */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", bgcolor: C.bsoft, mx: "4px", borderRadius: "3px 3px 0 0", overflow: "hidden" }}>
        {["AM", "PM"].map((lbl) => (
          <Box key={lbl} sx={{ bgcolor: C.cream, textAlign: "center", py: "2px" }}>
            <Typography sx={{ fontSize: 7, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>{lbl}</Typography>
          </Box>
        ))}
      </Box>

      {/* Hall grid */}
      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1px", bgcolor: C.bsoft, mx: "4px", mb: "4px", borderRadius: "0 0 3px 3px", overflow: "hidden", flex: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {HALLS.map((hall) => (
          <React.Fragment key={hall.key}>
            <HallSlot hallKey={hall.key as HallKey} hallColor={hall.color} info={hallSlots?.[hall.key as HallKey]?.AM} slot="AM" dateStr={dateStr} onFreeClick={onFreeSlotClick} onBookedClick={onBookedSlotClick} slotFilter={slotFilter} />
            <HallSlot hallKey={hall.key as HallKey} hallColor={hall.color} info={hallSlots?.[hall.key as HallKey]?.PM} slot="PM" dateStr={dateStr} onFreeClick={onFreeSlotClick} onBookedClick={onBookedSlotClick} slotFilter={slotFilter} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
});

const EmptyCell: React.FC = () => (
  <Box sx={{ borderRight: `1px solid ${C.bsoft}`, borderBottom: `1px solid ${C.bsoft}`, minHeight: { xs: 56, md: 134 }, bgcolor: C.cream, "&:nth-of-type(7n)": { borderRight: "none" } }} />
);

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE DAY CELL  (compact: just number + colored dots)
   ═══════════════════════════════════════════════════════════════════════════ */
const MobileDayCell: React.FC<{
  day: number; dateStr: string; isToday: boolean; isWeekend: boolean; isSelected: boolean;
  hallSlots: CalendarMap[string] | undefined;
  onDayClick: (d: string) => void;
}> = React.memo(({ day, dateStr, isToday, isWeekend, isSelected, hallSlots, onDayClick }) => {
  const bookingCount = useMemo(() => countDayBookings(hallSlots ? { [dateStr]: hallSlots } as CalendarMap : {}, dateStr), [hallSlots, dateStr]);
  const hasBookings = bookingCount > 0;

  return (
    <Box
      onClick={() => onDayClick(dateStr)}
      sx={{
        aspectRatio: "1",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: "pointer", borderRadius: "10px", mx: "2px", my: "1px",
        transition: "all 0.12s ease",
        bgcolor: isSelected ? C.highlight : "transparent",
        border: isSelected ? `2px solid ${C.gold}` : isToday ? `2px solid ${C.gold}` : "2px solid transparent",
        position: "relative",
      }}
    >
      <Typography sx={{
        fontSize: 14, fontWeight: isToday || isSelected ? 700 : 500, lineHeight: 1,
        color: isSelected ? C.navy : isToday ? C.gold : isWeekend ? C.slateLt : C.navy,
      }}>
        {day}
      </Typography>
      {/* Booking dot indicator */}
      {hasBookings && (
        <Box sx={{ display: "flex", gap: "2px", mt: "3px" }}>
          {bookingCount <= 3 ? (
            Array.from({ length: bookingCount }).map((_, i) => (
              <Box key={i} sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.redTx }} />
            ))
          ) : (
            <>
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.redTx }} />
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.redTx }} />
              <Typography sx={{ fontSize: 7, fontWeight: 700, color: C.redTx, lineHeight: 1 }}>+</Typography>
            </>
          )}
        </Box>
      )}
      {!hasBookings && (
        <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.greenBd, mt: "3px" }} />
      )}
    </Box>
  );
});

/* ═══════════════════════════════════════════════════════════════════════════
   SECTION TITLE (drawer)
   ═══════════════════════════════════════════════════════════════════════════ */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 10, fontWeight: 700, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.08em", mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
    <Box component="span" sx={{ width: 3, height: 14, bgcolor: C.gold, borderRadius: "2px", flexShrink: 0 }} />
    {children}
  </Typography>
);

/* ═══════════════════════════════════════════════════════════════════════════
   DAY DETAILS DRAWER
   ═══════════════════════════════════════════════════════════════════════════ */
const DayDetailsDrawer: React.FC<{
  open: boolean; selectedDate: string; details: DayDetails; dayBookings: BookingEntry[];
  onClose: () => void; onNavigate: (path: string) => void; isMobile: boolean;
}> = ({ open, selectedDate, details, dayBookings, onClose, onNavigate, isMobile }) => {
  const dateObj = new Date(selectedDate + "T12:00:00");
  const dateLong = dateObj.toLocaleDateString("en-CA", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const dateShort = dateObj.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });

  const drawerWidth = isMobile ? "100%" : 420;

  const content = (
    <>
      {/* Header */}
      <Box sx={{ bgcolor: C.navy, px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: `1px solid rgba(201,169,110,0.2)`, position: "sticky", top: 0, zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 15, sm: 17 }, fontWeight: 600, color: C.white, lineHeight: 1.3 }}>
              {isMobile ? dateShort : dateLong}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: C.gold }} />
                <Typography sx={{ fontSize: 11, color: C.gold }}>{details.totalBookings} booking{details.totalBookings !== 1 ? "s" : ""}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <MeetingRoomOutlinedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }} />
                <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{details.freeSlots}/8 free</Typography>
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.4)", "&:hover": { color: C.white, bgcolor: "rgba(255,255,255,0.08)" }, borderRadius: "8px" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ overflowY: "auto", flex: 1, bgcolor: C.cream }}>

        {/* Quick stats bar */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0, borderBottom: `1px solid ${C.bsoft}` }}>
          {[
            { label: "Bookings", value: details.totalBookings, icon: <CalendarTodayIcon sx={{ fontSize: 16, color: C.gold }} /> },
            { label: "Free Slots", value: details.freeSlots, icon: <MeetingRoomOutlinedIcon sx={{ fontSize: 16, color: C.greenTx }} /> },
            { label: "Conflicts", value: details.conflicts.length, icon: <WarningAmberIcon sx={{ fontSize: 16, color: details.conflicts.length > 0 ? C.redTx : C.slateLt }} /> },
          ].map((s, i) => (
            <Box key={i} sx={{ py: 1.5, textAlign: "center", bgcolor: C.white, borderRight: i < 2 ? `1px solid ${C.bsoft}` : "none" }}>
              {s.icon}
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.navy, lineHeight: 1.2, mt: 0.3 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: 9, fontWeight: 600, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Hall breakdown */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: `1px solid ${C.bsoft}`, bgcolor: C.white }}>
          <SectionTitle>Hall breakdown</SectionTitle>
          {HALLS.map((hall) => {
            const hk = hall.key as HallKey;
            const am = details.halls[hk].AM;
            const pm = details.halls[hk].PM;
            return (
              <Box key={hk} sx={{ mb: 1.5, "&:last-child": { mb: 0 } }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: "6px", bgcolor: hall.bg, border: `1.5px solid ${hall.color}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, color: hall.color }}>{hk}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{hall.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.slateLt }}>({hall.capacity})</Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, ml: 4 }}>
                  {(["AM", "PM"] as const).map((slot) => {
                    const d = slot === "AM" ? am : pm;
                    return (
                      <Box key={slot} sx={{ borderRadius: "8px", px: 1.5, py: 1, bgcolor: d.state === "free" ? C.greenBg : C.redBg, border: `1px solid ${d.state === "free" ? C.greenBd : C.redBd}`, transition: "all 0.12s" }}>
                        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: d.state === "free" ? C.greenTx : C.redTx, mb: 0.3 }}>{slot}</Typography>
                        {d.state === "free" ? (
                          <Typography sx={{ fontSize: 11, color: C.greenTx, fontWeight: 500 }}>Available</Typography>
                        ) : (
                          d.bookings.map((b) => (
                            <Typography key={b.id} sx={{ fontSize: 11, fontWeight: 500, color: C.redTx, cursor: "pointer", "&:hover": { textDecoration: "underline" } }} onClick={() => onNavigate(`/banquet/booking/${b.id}`)}>{b.name}</Typography>
                          ))
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Conflicts */}
        {details.conflicts.length > 0 && (
          <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: `1px solid ${C.bsoft}`, bgcolor: C.white }}>
            <SectionTitle>Conflicts</SectionTitle>
            {details.conflicts.map((c, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: C.redBg, border: `1px solid ${C.redBd}`, borderRadius: "8px", px: 1.5, py: 1, mb: 1, "&:last-child": { mb: 0 } }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: C.redTx }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.redTx }}>{c.hall} {c.slot} — {c.count} bookings</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Booking list */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: `1px solid ${C.bsoft}`, bgcolor: C.white }}>
          <SectionTitle>Bookings for this date</SectionTitle>
          {dayBookings.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 36, color: C.greenBd, mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: C.slateLt }}>No bookings on this date</Typography>
              <Typography sx={{ fontSize: 11, color: C.slateLt, mt: 0.5 }}>All halls are available</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {dayBookings.map((b) => {
                const custName = b.primaryCustomer ? [b.primaryCustomer.firstName, b.primaryCustomer.lastName].filter(Boolean).join(" ") || "—" : "—";
                const hallLabel = b.halls?.map((h) => h.hallLabel || h.hallKey).join(", ") || "—";
                return (
                  <Paper
                    key={b.id} elevation={0}
                    onClick={() => onNavigate(`/banquet/booking/${b.id}`)}
                    sx={{
                      p: 1.5, borderRadius: "10px", border: `1px solid ${C.bsoft}`, cursor: "pointer",
                      transition: "all 0.12s", "&:hover": { borderColor: C.gold, bgcolor: C.highlight, boxShadow: SHADOW_SM },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{b.banquetName || b.bookingRef || `#${b.id}`}</Typography>
                      <Chip label={b.status ? b.status.charAt(0).toUpperCase() + b.status.slice(1) : "—"} size="small" color={STATUS_COLOR[b.status ?? ""] ?? "default"} sx={{ fontSize: 10, height: 20 }} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 11, color: C.slate }}>{custName}</Typography>
                      <Typography sx={{ fontSize: 11, color: C.slateLt }}>{hallLabel}</Typography>
                      <Typography sx={{ fontSize: 11, color: C.slateLt }}>{SLOT_LABEL[b.timeSlot] ?? b.timeSlot}</Typography>
                      {b.guestCount && <Typography sx={{ fontSize: 11, color: C.slateLt }}>{b.guestCount} guests</Typography>}
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 1, justifyContent: "flex-end" }}>
                      <Tooltip title="View">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onNavigate(`/banquet/booking/${b.id}`); }} sx={{ bgcolor: C.cream, width: 28, height: 28 }}>
                          <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onNavigate(`/banquet/booking/edit/${b.id}`); }} sx={{ bgcolor: C.cream, width: 28, height: 28 }}>
                          <EditOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2, display: "flex", flexDirection: "column", gap: 1, bgcolor: C.cream }}>
          <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={() => onNavigate(`/banquet/booking/create?date=${selectedDate}`)}
            sx={{ bgcolor: C.gold, color: C.navy, fontWeight: 600, fontSize: 13, textTransform: "none", borderRadius: "10px", boxShadow: "none", py: 1.2, "&:hover": { bgcolor: "#b8935a", boxShadow: "none" } }}>
            New booking on this date
          </Button>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ borderColor: C.border, color: C.slate, fontWeight: 500, fontSize: 13, textTransform: "none", borderRadius: "10px", py: 1, "&:hover": { borderColor: C.navy, bgcolor: C.highlight } }}>
            Close panel
          </Button>
        </Box>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        disableSwipeToOpen
        PaperProps={{
          sx: { borderRadius: "20px 20px 0 0", maxHeight: "92vh", display: "flex", flexDirection: "column" },
        }}
      >
        {/* Pull indicator */}
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: C.bsoft }} />
        </Box>
        {content}
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      anchor="right" open={open} onClose={onClose} variant="persistent"
      sx={{
        width: open ? 420 : 0, flexShrink: 0,
        "& .MuiDrawer-paper": { width: 420, boxSizing: "border-box", border: "none", boxShadow: `-4px 0 24px rgba(13,27,42,0.12)`, display: "flex", flexDirection: "column" },
      }}
    >
      {content}
    </Drawer>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN CALENDAR PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
const BanquetCalendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { showErrorMessage } = useNotifier();

  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [rawBookings, setRawBookings] = useState<BookingEntry[]>([]);
  const [calMap, setCalMap] = useState<CalendarMap>({});
  const [isFetching, setIsFetching] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slotFilter, setSlotFilter] = useState<"all" | "free" | "booked">("all");
  const drawerOpen = selectedDate !== null;

  const [getBookings] = useGetBanquetBookingsMutation();

  const fetchMonth = useCallback(async (y: number, m: number) => {
    setIsFetching(true);
    try {
      const lastDay = new Date(y, m + 1, 0).getDate();
      const res = await (getBookings as any)({ dateFrom: `${y}-${pad2(m + 1)}-01`, dateTo: `${y}-${pad2(m + 1)}-${pad2(lastDay)}` }).unwrap();
      if (res.status && Array.isArray(res.data)) {
        const data = res.data as BookingEntry[];
        setRawBookings(data);
        setCalMap(buildCalendarMap(data));
      } else { setRawBookings([]); setCalMap({}); }
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "Failed to load calendar");
      setRawBookings([]); setCalMap({});
    } finally { setIsFetching(false); }
  }, [getBookings, showErrorMessage]);

  useEffect(() => { fetchMonth(year, month); }, [year, month, fetchMonth]);

  const prevMonth = () => { setSelectedDate(null); if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { setSelectedDate(null); if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };
  const goToday = () => { setSelectedDate(null); setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = new Date(year, month, 1).toLocaleString("default", { month: "long", year: "numeric" });
  const todayStr = fmtDate(today.getFullYear(), today.getMonth(), today.getDate());

  const dayDetails = useMemo(() => selectedDate ? getDayDetails(calMap, selectedDate) : null, [calMap, selectedDate]);
  const dayBookings = useMemo(() => selectedDate ? rawBookings.filter(b => (b.eventDate?.split("T")[0] ?? "") === selectedDate) : [], [rawBookings, selectedDate]);

  // Monthly summary stats
  const monthStats = useMemo(() => {
    const total = rawBookings.length;
    const orders = rawBookings.filter(b => b.status === "order").length;
    const quotes = rawBookings.filter(b => b.status === "quote").length;
    return { total, orders, quotes };
  }, [rawBookings]);

  const handleDayClick = useCallback((dateStr: string) => setSelectedDate(prev => prev === dateStr ? null : dateStr), []);
  const handleFreeSlotClick = useCallback((dateStr: string, slot: "AM" | "PM", hallKey: HallKey) => navigate(`/banquet/booking/create?date=${dateStr}&slot=${slot}&hall=${hallKey}`), [navigate]);
  const handleBookedSlotClick = useCallback((dateStr: string) => setSelectedDate(dateStr), []);
  const handleDrawerNav = useCallback((path: string) => navigate(path), [navigate]);
  const handleDrawerClose = useCallback(() => setSelectedDate(null), []);

  const dowLabels = isXs ? DOW_TINY : isMobile ? DOW_SHORT : DOW_SHORT;

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1, sm: 1, md: 2 } }}>
      <Box sx={{ display: "flex", pb: { xs: 8, sm: 10 } }}>

        <Box sx={{ flex: 1, minWidth: 0, transition: "margin 0.25s", mr: drawerOpen && !isMobile ? "420px" : 0 }}>

          {/* ═══ MONTH SUMMARY CARDS ═══ */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", md: "repeat(3, 1fr)" }, gap: { xs: 1, md: 1.5 }, mb: { xs: 1.5, md: 2 } }}>
            {[
              { label: "This Month", value: monthStats.total, sub: "Total bookings", color: C.navy, icon: <CalendarTodayIcon sx={{ fontSize: { xs: 16, md: 20 } }} /> },
              { label: "Confirmed", value: monthStats.orders, sub: "Active orders", color: C.greenTx, icon: <EventIcon sx={{ fontSize: { xs: 16, md: 20 } }} /> },
              { label: "Pending", value: monthStats.quotes, sub: "Awaiting confirmation", color: C.amberTx, icon: <PeopleAltOutlinedIcon sx={{ fontSize: { xs: 16, md: 20 } }} /> },
            ].map((s, i) => (
              <Paper key={i} elevation={0} sx={{ bgcolor: C.white, border: `1px solid ${C.bsoft}`, borderRadius: { xs: "10px", md: "12px" }, p: { xs: 1.5, md: 2 }, boxShadow: SHADOW_SM, position: "relative", overflow: "hidden" }}>
                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${C.gold}, transparent)`, opacity: 0.5 }} />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Box sx={{ color: s.color }}>{s.icon}</Box>
                  <Typography sx={{ fontSize: { xs: 9, md: 10.5 }, fontWeight: 600, color: C.slateLt, textTransform: "uppercase", letterSpacing: "0.07em" }}>{s.label}</Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 700, color: C.navy, lineHeight: 1 }}>{s.value}</Typography>
                <Typography sx={{ fontSize: { xs: 10, md: 11.5 }, color: C.slate, mt: 0.3, display: { xs: "none", sm: "block" } }}>{s.sub}</Typography>
              </Paper>
            ))}
          </Box>

          {/* ═══ CALENDAR CARD ═══ */}
          <Paper elevation={0} sx={{ bgcolor: C.white, border: `1px solid ${C.bsoft}`, borderRadius: { xs: "12px", md: "14px" }, overflow: "hidden", boxShadow: SHADOW_SM }}>

            {/* Header */}
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              px: { xs: 1.5, md: 3 }, py: { xs: 1, md: 1.5 },
              borderBottom: `1px solid ${C.bsoft}`, bgcolor: C.cream,
              flexWrap: "wrap", gap: 1,
            }}>
              {/* Month nav */}
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 } }}>
                <IconButton size="small" onClick={prevMonth} sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: "8px", width: { xs: 30, md: 34 }, height: { xs: 30, md: 34 }, "&:hover": { bgcolor: C.navy, color: C.gold, borderColor: C.navy } }}>
                  <ChevronLeftIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                </IconButton>
                <Typography sx={{ fontWeight: 700, fontSize: { xs: 14, sm: 16, md: 18 }, color: C.navy, minWidth: { xs: 120, md: 180 }, textAlign: "center", userSelect: "none" }}>{monthLabel}</Typography>
                <IconButton size="small" onClick={nextMonth} sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: "8px", width: { xs: 30, md: 34 }, height: { xs: 30, md: 34 }, "&:hover": { bgcolor: C.navy, color: C.gold, borderColor: C.navy } }}>
                  <ChevronRightIcon sx={{ fontSize: { xs: 16, md: 20 } }} />
                </IconButton>
                <IconButton size="small" onClick={goToday} sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: "8px", width: { xs: 30, md: 34 }, height: { xs: 30, md: 34 }, ml: 0.5, "&:hover": { bgcolor: C.gold, color: C.navy, borderColor: C.gold } }}>
                  <TodayIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                </IconButton>
                {isFetching && <CircularProgress size={14} thickness={5} sx={{ color: C.gold, ml: 0.5 }} />}
              </Box>

              {/* Right: filters + new booking */}
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, md: 1 }, flexWrap: "wrap" }}>
                {!isXs && (
                  <Box sx={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: `1px solid ${C.border}` }}>
                    {([
                      { key: "all" as const, label: "All", color: C.navy },
                      { key: "free" as const, label: "Free", color: C.greenTx },
                      { key: "booked" as const, label: "Booked", color: C.redTx },
                    ]).map(f => (
                      <Box key={f.key} onClick={() => setSlotFilter(f.key)}
                        sx={{
                          px: 1.2, py: 0.4, fontSize: 10, fontWeight: 600, cursor: "pointer",
                          bgcolor: slotFilter === f.key ? C.navy : "transparent",
                          color: slotFilter === f.key ? C.white : f.color,
                          transition: "all 0.12s",
                          "&:hover": { bgcolor: slotFilter === f.key ? C.navy : C.cream },
                        }}>{f.label}</Box>
                    ))}
                  </Box>
                )}
                <Button variant="contained" size="small" startIcon={!isXs ? <AddIcon /> : undefined} onClick={() => navigate("/banquet/booking/create")}
                  sx={{ textTransform: "none", fontWeight: 600, fontSize: { xs: 11, md: 12 }, bgcolor: C.gold, color: C.navy, px: { xs: 1.5, md: 2 }, height: { xs: 28, md: 32 }, borderRadius: "8px", boxShadow: "none", minWidth: 0, "&:hover": { bgcolor: "#b8935a", boxShadow: "none" } }}>
                  {isXs ? <AddIcon sx={{ fontSize: 16 }} /> : "New booking"}
                </Button>
              </Box>
            </Box>

            {/* Hall key bar — hidden on xs */}
            {!isXs && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 1.5, md: 3 }, py: 0.8, borderBottom: `1px solid ${C.bsoft}`, bgcolor: C.navy, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 9, color: "rgba(255,255,255,0.3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em" }}>Halls:</Typography>
                {HALLS.map(h => (
                  <Chip key={h.key} size="small" label={`${h.key} = ${h.label} (${h.capacity})`} sx={{ height: 20, fontSize: 9.5, fontWeight: 500, bgcolor: "rgba(201,169,110,0.15)", color: C.gold, border: "1px solid rgba(201,169,110,0.25)", borderRadius: "20px" }} />
                ))}
                {!isMobile && <Typography sx={{ fontSize: 9.5, color: "rgba(255,255,255,0.25)" }}>Click day to see details &middot; Click free slot to book</Typography>}
              </Box>
            )}

            {/* DOW header */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${C.bsoft}` }}>
              {dowLabels.map((d, i) => (
                <Box key={i} sx={{ bgcolor: C.cream, textAlign: "center", py: { xs: 0.5, md: 1 }, borderRight: `1px solid ${C.bsoft}`, "&:last-child": { borderRight: "none" } }}>
                  <Typography sx={{ fontSize: { xs: 10, sm: 10.5, md: 11 }, fontWeight: 600, color: (i === 0 || i === 6) ? C.slateLt : C.slate, textTransform: "uppercase", letterSpacing: "0.05em" }}>{d}</Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar grid */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
              {Array.from({ length: firstDow }).map((_, i) => <EmptyCell key={`e-${i}`} />)}

              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dateStr = fmtDate(year, month, day);
                const dow = new Date(year, month, day).getDay();
                const isToday = dateStr === todayStr;
                const isWeekend = dow === 0 || dow === 6;

                if (isXs) {
                  return (
                    <MobileDayCell
                      key={day} day={day} dateStr={dateStr} isToday={isToday} isWeekend={isWeekend}
                      isSelected={selectedDate === dateStr} hallSlots={calMap[dateStr]} onDayClick={handleDayClick}
                    />
                  );
                }

                return (
                  <DayCell
                    key={day} day={day} dateStr={dateStr} isToday={isToday} isWeekend={isWeekend}
                    isSelected={selectedDate === dateStr} hallSlots={calMap[dateStr]}
                    onDayClick={handleDayClick} onFreeSlotClick={handleFreeSlotClick} onBookedSlotClick={handleBookedSlotClick}
                    slotFilter={slotFilter}
                  />
                );
              })}

              {(() => {
                const lastDow = new Date(year, month, daysInMonth).getDay();
                const trailing = lastDow === 6 ? 0 : 6 - lastDow;
                return Array.from({ length: trailing }).map((_, i) => <EmptyCell key={`t-${i}`} />);
              })()}
            </Box>
          </Paper>
        </Box>

        {/* Drawer */}
        {selectedDate && dayDetails && (
          <DayDetailsDrawer
            open={drawerOpen} selectedDate={selectedDate} details={dayDetails}
            dayBookings={dayBookings} onClose={handleDrawerClose} onNavigate={handleDrawerNav} isMobile={isMobile}
          />
        )}
      </Box>
    </Container>
  );
};

export default BanquetCalendar;
